/// <amd-module name="VSS/Features/PlatformUI/ContributedTab" />
import * as React from "react";
import { IReadonlyObservableArray, ObservableCollection, ObservableLike } from "VSS/Core/Observable";
import { ILegacyPlatformService } from "VSS/Legacy/Types";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { WrappedComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { traceWarning } from "VSS/Platform/Trace";
import { IVssContributedTab } from "VSSUI/Tabs.Types";
import { Constants } from "./Constants";
import { ContributedContent } from "./ContributedContent";
import { getContributedItems, wrapExternalObservable } from "./ContributedItemUtil";
import { getMenuItems } from "./ContributedMenu";

interface ITabSpec extends IVssContributedTab {
    /**
     * Optional dependencies that need to be resolved when rendering this component.
     */
    dependencies?: string[];
    /**
     * Contribution id of the content ot display.
     */
    componentType: string;
    /**
     * Props to pass to the component
     */
    componentProperties?: { [prop: string]: any };
}

interface ITabContributionDescriptor {
    id: string;
    contribution: IVssContributedTab;
}

/**
 * Optional settings to be used on contributed tabs
 */
export interface IContributedTabOptions<TContextualData = {}> {
    /**
     * Optional contextual menu data that is passed to tab providers and as the "context" prop to the tab's content component
     */
    contextualData?: TContextualData;

    /**
     * Optional id of the default route to use for tabs that don't specify any route id (e.g. third-party tabs)
     */
    defaultRouteId?: string;

    /**
     * If true, perform an FPS (fast page switch) when switching between any two tabs
     */
    fpsRequired?: boolean;

    /**
     * Component to display while the pivot contents are loading
     */
    loadingComponent?: () => JSX.Element;

    /**
     * Function to initalize other required props from legacy tab
     */
    preProcessTabContribution?: (tab: IVssContributedTab | ILegacyContributedTab) => void;

    /**
     * Additional props to pass to each contributed tab's component.
     */
    props?: { [prop: string]: any };

    /**
     * The route parameter key that is used to represent the tab. "contributionId" is used if not specified.
     */
    routeStateKey?: string;

    /**
     * Route values to use (in conjunction with those on the tab contribution) for determining
     * the url of the tab.
     */
    routeValues?: { [routeParameter: string]: string };

    /**
     * If true, update the page's URL when the pivot has changed
     */
    updateUrlOnPivotChange?: boolean;
}

/**
 * To supply tabs to a contributed TabBar, a service must implement ITabService
 */
export interface ITabService<TContextualData = {}> {
    /**
     * getTabs is called on each tab service to get the group of tabs to display
     *
     * @param tabGroupId Id of the group of tabs being rendered
     * @param contextualData Optional contextual menu data provided by the UI showing the tabs
     */
    getTabs: (tabGroupId: string, contextualData?: TContextualData) => IVssContributedTab[] | IReadonlyObservableArray<IVssContributedTab> | null;
}

interface IExternalTabService<TContextualData = {}> {
    getTabs: (
        tabGroupId: string,
        contextualData?: TContextualData
    ) => Promise<IVssContributedTab[] | IReadonlyObservableArray<IVssContributedTab> | null>;
}

/**
 * Legacy tab properties
 */
export interface ILegacyContributedTab extends ITabSpec {
    /**
     * Item key from contribtion
     */
    itemKey?: string;

    /**
     * Module content provided in contribution.
     */
    moduleContent?: string;
}

interface ITabWithCommandBarMenuId {
    /**
     * A command bar menu id can be provided in order to provide a contributable menu
     * for command bar items that are specific to this tab
     */
    commandBarMenuId?: string;
}

interface IContributedTab {
    isDisabled?: (context?: any) => boolean | Promise<boolean>;
    isInvisible?: boolean | ((context?: any) => boolean | Promise<boolean>);
    name: string | Promise<string> | ((context?: any) => string | Promise<string>);
    title?: string | Promise<string> | ((context?: any) => string | Promise<string>);
    groupKey?: string | Promise<string> | ((context?: any) => string | Promise<string>);
}

interface IVssDynamicContributedTab extends IVssContributedTab {
    dynamic: boolean;
}

/**
 * Initalizes tab contribution params required to display a tab.
 *
 * @param pageContext Page context
 * @param options Options for processing tab
 * @param tab Contribution tab data
 * @param contributionId Contribution id of the tab
 */
export function processTabContribution(
    pageContext: IVssPageContext,
    options: IContributedTabOptions,
    tab: IVssContributedTab | ILegacyContributedTab,
    contributionId: string
): IVssContributedTab {
    if (options.preProcessTabContribution) {
        options.preProcessTabContribution(tab);
    }

    if (!tab.render) {
        const legacyTab: ILegacyContributedTab = tab as ILegacyContributedTab;

        const VssLegacyContributionContentHost = (props: { contribution: string | undefined; context: any }) => {
            return (
                <WrappedComponent
                    key={legacyTab.moduleContent}
                    dependencies={["ms.vss-web.legacy-content", "ms.vss-web.legacy-vss-ui-content"]}
                    wrappedType="legacy"
                    loadingComponent={options.loadingComponent}
                    wrappedProps={{
                        componentType: "vssLegacyContributionContentHost",
                        modules: ["VSSPreview/Providers/ContentHost"],
                        dependentContributionIds: legacyTab.dependencies,
                        wrappedProps: {
                            className: "flex-grow",
                            context: props.context,
                            contribution: props.contribution
                        }
                    }}
                />
            );
        };

        // Handle legacy first-party tabs, which are identified by a "moduleContent" property
        if (legacyTab.moduleContent) {
            if (tab.fpsRequired || options.fpsRequired) {
                tab.render = () => (
                    <WrappedComponent
                        key={legacyTab.moduleContent}
                        dependencies={["ms.vss-web.legacy-content", "ms.vss-web.legacy-vss-ui-content"]}
                        wrappedType="legacy"
                        loadingComponent={options.loadingComponent}
                        wrappedProps={{
                            className: "flex-grow",
                            componentType: "legacyHubShimComponent",
                            dependentContributionIds: legacyTab.dependencies,
                            modules: ["VSSPreview/Flux/Components/ExternalHubShim"],
                            contributionId: legacyTab.moduleContent
                        }}
                    />
                );
            } else {
                tab.render = () => <VssLegacyContributionContentHost contribution={legacyTab.moduleContent} context={pageContext} />;
            }
        } else if ((legacyTab as any).content) {
            tab.render = () => <VssLegacyContributionContentHost contribution={legacyTab.id} context={options.contextualData} />;
        } else {
            tab.render = () => (
                <ContributedContent
                    key={contributionId}
                    className="flex-grow"
                    contributionId={contributionId}
                    contribution={tab}
                    dependencies={(tab as ITabSpec).dependencies}
                    componentProperties={{
                        context: options.contextualData,
                        ...options.props
                    }}
                    loadingComponent={options.loadingComponent}
                />
            );
        }
    }

    if (!tab.id) {
        tab.id = contributionId;
    }

    if (tab.routeId) {
        const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
        tab.url = locationService.routeUrl(tab.routeId, { ...options.routeValues, ...tab.routeValues });
    } else if (options.defaultRouteId) {
        const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
        const routeValues: { [key: string]: string } = {};
        routeValues[options.routeStateKey || "contributionId"] = tab.id || contributionId;
        tab.url = locationService.routeUrl(options.defaultRouteId, routeValues);
    } else if (options.routeStateKey) {
        const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeValues: { [key: string]: string } = {};
        routeValues[options.routeStateKey] = tab.id;
        tab.url = historyService.generateUrl(routeValues, StateMergeOptions.routeValues);
    }

    if (tab.fpsRequired || options.fpsRequired) {
        tab.onBeforeTabChange = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | null, tabId: string, url?: string) => {
            if (url) {
                FastPageSwitch(pageContext, url, true);
                ev && ev.preventDefault();
                return false;
            } else {
                return true;
            }
        };
    } else if (tab.url && options.updateUrlOnPivotChange) {
        tab.onBeforeTabChange = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | null, tabId: string, url?: string) => {
            const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
            historyService.pushState(undefined, tab.url && ObservableLike.getValue(tab.url));
            return true;
        };
    }

    const tabWithMenu = tab as ITabWithCommandBarMenuId;
    if (tabWithMenu.commandBarMenuId && !tab.commandBarItems) {
        tab.commandBarItems = getMenuItems(pageContext, tabWithMenu.commandBarMenuId, { tabId: tab.id });
    }

    return tab;
}

/**
 * Gets an observable array of tabs for the specified tabs contribution id. These include
 * contributed tabs that target the tabGroupId as well as those with a tabGroupId property that equals
 * the supplied tabGroupId.
 *
 * @param pageContext Page context
 * @param tabGroupId Contribution id of the tabs group
 * @param options Options for processing each tab
 */
export function getTabs<TContextualData = {}>(
    pageContext: IVssPageContext,
    tabGroupId: string,
    options: IContributedTabOptions<TContextualData> = {}
): IReadonlyObservableArray<IVssContributedTab> {
    const tabsCollection = new ObservableCollection<IVssContributedTab>();
    addTabs(pageContext, tabsCollection, tabGroupId, options);
    return tabsCollection;
}

/**
 * Adds tabs for the specified tabs contribution id to the given observable collection. These include
 * contributed tabs that target the tabGroupId as well as those with a tabGroupId property that equals
 * the supplied tabGroupId.
 *
 * @param pageContext Page context
 * @param collection Observable collection of tabs to add to
 * @param tabGroupId Contribution id of the tabGroupId
 * @param options Options for processing each tab
 */
export function addTabs<TContextualData = {}>(
    pageContext: IVssPageContext,
    collection: ObservableCollection<IVssContributedTab>,
    tabGroupId: string,
    options: IContributedTabOptions<TContextualData> = {}
): void {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");

    // Load static tab contributions
    const staticTabContributions = getContributedItems<IVssContributedTab | ITabSpec>(pageContext, tabGroupId, "tabGroupId", "ms.vss-web.tab");
    for (const tabContribution of staticTabContributions) {
        const tab = processTabContribution(pageContext, options, tabContribution.contribution, tabContribution.id) as IVssDynamicContributedTab;
        if (tab.dynamic) {
            if (!isFeatureFlagEnabled(pageContext, Constants.DisableDynamicTabsProcessing, false)) {
                processDynamicTabContribution(tabContribution.id, tab).then(contributedTab => {
                    if (contributedTab) {
                        collection.push([contributedTab]);
                    }
                });
            }
        } else {
            collection.push([tab]);
        }
    }

    // Load dynamic tab contributions. Synchronous providers first
    const tabProviderContributions = getContributedItems(pageContext, tabGroupId, "tabGroupId", "ms.vss-web.tab-provider");

    for (const tabProvider of tabProviderContributions) {
        // Check if tabs can be added synchronously
        const tabsService = contributionService.getServiceSync<ITabService>(tabProvider.id, true);
        if (tabsService) {
            const tabs = tabsService.getTabs(tabGroupId, options.contextualData);
            if (tabs) {
                collection.push(tabs, c => processTabContribution(pageContext, options, c, c.id));
            }
        } else {
            // Add tabs from async services
            contributionService.getService<IExternalTabService | ITabService>(tabProvider.id).then(tabsService => {
                if (tabsService) {
                    const tabs = tabsService.getTabs(tabGroupId, options.contextualData);
                    const externalTabs = tabs as Promise<IVssContributedTab[] | IReadonlyObservableArray<IVssContributedTab> | null>;
                    if (externalTabs && typeof externalTabs.then === "function") {
                        externalTabs.then(
                            resolvedTabs => {
                                if (resolvedTabs) {
                                    collection.push(wrapExternalObservable(resolvedTabs), c => {
                                        wrapExternalObservable(c.name);
                                        wrapExternalObservable(c.badgeCount);
                                        return processTabContribution(pageContext, options, c, c.id);
                                    });
                                }
                            },
                            (e: Error) => {
                                const message = `Failed to load tabs for ${tabGroupId}: ${JSON.stringify(e || "")}`;
                                traceWarning(pageContext, "extensions", "Contributedtab", message, { tabGroupId });
                                console.warn(message);
                            }
                        );
                    } else if (tabs) {
                        collection.push(tabs as IVssContributedTab[] | IReadonlyObservableArray<IVssContributedTab>, c =>
                            processTabContribution(pageContext, options, c, c.id)
                        );
                    }
                }
            });
        }
    }

    async function getDynamicTabInstance(contributionInstanceId: string): Promise<IContributedTab | undefined> {
        try {
            const legacyService = pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
            const [contributionControls] = await legacyService.requireModules(["VSS/Contributions/Controls"]);
            const contributionHost = await contributionControls.getBackgroundHost(contributionInstanceId);
            const instance = await contributionHost.getRegisteredInstance(contributionInstanceId, {});
            return instance;
        } catch (error) {
            return;
        }
    }

    async function getDynamicTab(
        instance: IContributedTab,
        tab: IVssContributedTab,
        options: IContributedTabOptions<TContextualData> = {}
    ): Promise<IContributedTab | undefined> {
        if (!instance) {
            return;
        }
        const context = options.contextualData;

        const isInvisible = typeof instance.isInvisible === "function" ? await instance.isInvisible(context) : instance.isInvisible ?? false;
        if (isInvisible) {
            return { isInvisible } as IContributedTab;
        }

        const [name, title, groupKey] = await Promise.all([
            typeof instance.name === "function" ? instance.name(context) : instance.name ?? tab.name,
            typeof instance.title === "function" ? instance.title(context) : instance.title,
            typeof instance.groupKey === "function" ? instance.groupKey(context) : instance.groupKey
        ]);

        const isDisabled = await instance.isDisabled?.(context);

        return { isDisabled, isInvisible, name, title, groupKey } as IContributedTab;
    }

    async function processDynamicTabContribution(contributionId: string, tab: IVssDynamicContributedTab): Promise<IVssContributedTab | undefined> {
        const dynamicTabInstance = await getDynamicTabInstance(contributionId);
        if (dynamicTabInstance) {
            const dynamicTab = await getDynamicTab(dynamicTabInstance, tab, options);
            if (dynamicTab && !dynamicTab.isInvisible) {
                if (dynamicTab?.name) {
                    tab.name = dynamicTab.name as string;
                }
                return tab;
            }
        }
        return;
    }
}
